import { FormattedMessage } from 'react-intl'
import { keepPreviousData } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'

import { Button } from '@gto/ui/button'
import { Typography } from '@gto/ui/typography'

import { useDealPublicDealsOverviewRetrieve } from '@/api/gen'
import { AbsoluteSuitsOnCorners } from '@/components/absolute-suits-on-corners'
import { Loader } from '@/components/loader'
import { NoDealsPlaceholder } from '@/components/no-deals-placeholder'
import { sendGaEvent } from '@/gtag'
import { DealsList } from '@/pages/deals/components/deals-list'

export const DealsSection = ({ homePage = true }: { homePage?: boolean }) => {
  const { data, isFetching } = useDealPublicDealsOverviewRetrieve({
    query: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      enabled: true,
      placeholderData: keepPreviousData,
    },
  })
  const deals = data?.deals ?? []

  const countryName = data?.user_location_display ?? ''

  return (
    <section className="relative mt-12 flex flex-col gap-16 p-12 sm:gap-8 sm:px-4">
      <AbsoluteSuitsOnCorners />
      {(!isFetching && Array.isArray(deals) && deals.length === 0) || !Array.isArray(deals) ? (
        <NoDealsPlaceholder />
      ) : (
        <>
          <Typography
            className="whitespace-pre-line text-center"
            color="primary"
            component="h2"
            variant="h3"
          >
            <FormattedMessage
              id={homePage ? 'home.dealsSection.title' : 'rewards.extraDealsSection.title'}
              values={{
                country: countryName,
                wrapper: (chunks) => {
                  if (!countryName) {
                    return null
                  }

                  return (
                    <Typography
                      className="font-semibold [font-size:inherit]"
                      component="span"
                    >
                      {chunks}
                    </Typography>
                  )
                },
              }}
            />
          </Typography>

          {isFetching ? (
            <Loader />
          ) : (
            <>
              <DealsList deals={deals} />
              <div className="text-center">
                <Button
                  as={Link}
                  color={homePage ? 'primary' : 'secondary'}
                  href="/app/deals"
                  size="large"
                  variant="contained"
                  onClick={() => {
                    sendGaEvent({
                      event: 'home_view_all_deals_top',
                    })
                  }}
                >
                  <FormattedMessage id="common.controls.viewAllDeals" />
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}
